import * as THREE from 'three';
import { FillPattern } from '../../types/floorplannerTypes';
import { getFillPatternDefinition } from '../../utils/fillPatterns';
import { getPatternTexture } from '../../utils/patternTextures';

// Vertex shader for pattern fill
const patternFillVertexShader = `
  varying vec2 vPatternCoord;
  uniform bool useWorldSpace;
  uniform bool useLocalPositionCoords;

  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);

    if (useWorldSpace) {
      vPatternCoord = worldPos.xy;
    } else if (useLocalPositionCoords) {
      vPatternCoord = position.xy;
    } else {
      vec2 objectOrigin = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xy;
      vPatternCoord = worldPos.xy - objectOrigin;
    }

    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Fragment shader for pattern fill
const patternFillFragmentShader = `
  uniform float patternScale;
  uniform float strokeWidth;
  uniform int patternType;
  uniform sampler2D patternTexture;
  uniform bool usePatternTexture;
  uniform vec3 fillColor;
  uniform float lineDarkness;
  uniform float opacity;

  varying vec2 vPatternCoord;

  const float PI = 3.14159265358979323846;

  float patternAA() {
    vec2 baseUv = vPatternCoord / patternScale;
    return max(max(length(dFdx(baseUv)), length(dFdy(baseUv))), 0.0008);
  }

  float strokeMask(float distanceValue, float halfWidth) {
    float aa = patternAA();
    return 1.0 - smoothstep(halfWidth - aa, halfWidth + aa, distanceValue);
  }

  float strokeMaskAA(float distanceValue, float halfWidth, float aa) {
    return 1.0 - smoothstep(halfWidth - aa, halfWidth + aa, distanceValue);
  }

  float coordAA(vec2 coord) {
    return max(max(length(dFdx(coord)), length(dFdy(coord))), 0.0008);
  }

  float scalarAA(float coord) {
    return max(max(abs(dFdx(coord)), abs(dFdy(coord))), 0.0008);
  }

  float sdSegment(vec2 p, vec2 a, vec2 b) {
    vec2 pa = p - a;
    vec2 ba = b - a;
    float h = clamp(dot(pa, ba) / max(dot(ba, ba), 0.0001), 0.0, 1.0);
    return length(pa - ba * h);
  }

  float segmentMask(vec2 p, vec2 a, vec2 b, float halfWidth) {
    return strokeMask(sdSegment(p, a, b), halfWidth);
  }

  float segmentMaskAA(vec2 p, vec2 a, vec2 b, float halfWidth, float aa) {
    return strokeMaskAA(sdSegment(p, a, b), halfWidth, aa);
  }

  float sdBox(vec2 p, vec2 halfSize) {
    vec2 d = abs(p) - halfSize;
    return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
  }

  float rectBorderMask(vec2 p, vec2 minCorner, vec2 maxCorner, float halfWidth) {
    vec2 center = (minCorner + maxCorner) * 0.5;
    vec2 halfSize = (maxCorner - minCorner) * 0.5;
    return strokeMask(abs(sdBox(p - center, halfSize)), halfWidth);
  }

  float rectBorderMaskAA(vec2 p, vec2 minCorner, vec2 maxCorner, float halfWidth, float aa) {
    vec2 center = (minCorner + maxCorner) * 0.5;
    vec2 halfSize = (maxCorner - minCorner) * 0.5;
    return strokeMaskAA(abs(sdBox(p - center, halfSize)), halfWidth, aa);
  }

  float wrappedRectBorderMask(
    vec2 p,
    vec2 minCorner,
    vec2 maxCorner,
    float halfWidth,
    vec2 repeatSize
  ) {
    float mask = 0.0;

    for (int offsetY = -1; offsetY <= 1; offsetY++) {
      for (int offsetX = -1; offsetX <= 1; offsetX++) {
        vec2 offset = vec2(float(offsetX) * repeatSize.x, float(offsetY) * repeatSize.y);
        mask = max(mask, rectBorderMask(p, minCorner + offset, maxCorner + offset, halfWidth));
      }
    }

    return mask;
  }

  float wrappedRectBorderMaskAA(
    vec2 p,
    vec2 minCorner,
    vec2 maxCorner,
    float halfWidth,
    vec2 repeatSize,
    float aa
  ) {
    float mask = 0.0;

    for (int offsetY = -1; offsetY <= 1; offsetY++) {
      for (int offsetX = -1; offsetX <= 1; offsetX++) {
        vec2 offset = vec2(float(offsetX) * repeatSize.x, float(offsetY) * repeatSize.y);
        mask = max(mask, rectBorderMaskAA(p, minCorner + offset, maxCorner + offset, halfWidth, aa));
      }
    }

    return mask;
  }

  vec2 rotate2D(vec2 p, float angle) {
    float s = sin(angle);
    float c = cos(angle);
    return mat2(c, -s, s, c) * p;
  }

  vec2 hash22(vec2 p) {
    p = vec2(
      dot(p, vec2(127.1, 311.7)),
      dot(p, vec2(269.5, 183.3))
    );
    return fract(sin(p) * 43758.5453123);
  }

  float hash21(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float lineStripeMask(float coordinate, float spacing, float halfWidth) {
    float d = abs(fract(coordinate / spacing + 0.5) - 0.5) * spacing;
    return strokeMask(d, halfWidth);
  }

  float lineStripeMaskAA(float coordinate, float spacing, float halfWidth, float aa) {
    float d = abs(fract(coordinate / spacing + 0.5) - 0.5) * spacing;
    return strokeMaskAA(d, halfWidth, aa);
  }

  float triangleMask(vec2 p, vec2 center, float size, float rotation, float halfWidth) {
    float mask = 0.0;
    vec2 a = center + rotate2D(vec2(0.0, -size), rotation);
    vec2 b = center + rotate2D(vec2(size * 0.866, size * 0.5), rotation);
    vec2 c = center + rotate2D(vec2(-size * 0.866, size * 0.5), rotation);
    mask = max(mask, segmentMask(p, a, b, halfWidth));
    mask = max(mask, segmentMask(p, b, c, halfWidth));
    mask = max(mask, segmentMask(p, c, a, halfWidth));
    return mask;
  }

  float randomDotPattern(vec2 uv, float density, float radius, float seed) {
    vec2 grid = uv * density;
    vec2 baseCell = floor(grid);
    vec2 local = fract(grid);
    float mask = 0.0;

    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 offset = vec2(float(i), float(j));
        vec2 neighbor = baseCell + offset;
        vec2 point = hash22(neighbor + seed);
        vec2 delta = offset + point - local;
        mask = max(mask, strokeMask(length(delta), radius));
      }
    }

    return mask;
  }

  float granularDotPattern(
    vec2 uv,
    float density,
    float radius,
    float seed,
    float occupancy,
    float pairChance
  ) {
    vec2 grid = uv * density;
    vec2 baseCell = floor(grid);
    vec2 local = fract(grid);
    float mask = 0.0;

    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 offset = vec2(float(i), float(j));
        vec2 neighbor = baseCell + offset;
        float enabled = step(hash21(neighbor + vec2(seed * 0.71, seed * 1.13)), occupancy);
        vec2 point = hash22(neighbor + seed);
        float size = mix(0.7, 1.35, hash21(neighbor + vec2(seed * 1.91, seed * 2.37)));
        vec2 delta = offset + point - local;
        mask = max(mask, strokeMask(length(delta), radius * size) * enabled);

        float paired = step(pairChance, hash21(neighbor + vec2(seed * 2.73, seed * 3.19)));
        if (paired > 0.0) {
          float angle = hash21(neighbor + vec2(seed * 3.91, seed * 4.27)) * PI * 2.0;
          float distanceOffset = 0.14 + 0.1 * hash21(neighbor + vec2(seed * 4.83, seed * 5.11));
          vec2 secondaryPoint = point + vec2(cos(angle), sin(angle)) * distanceOffset;
          vec2 secondaryDelta = offset + secondaryPoint - local;
          float secondarySize = mix(0.55, 0.9, hash21(neighbor + vec2(seed * 5.41, seed * 5.87)));
          mask = max(mask, strokeMask(length(secondaryDelta), radius * secondarySize) * enabled);
        }
      }
    }

    return mask;
  }

  float scatteredDashPattern(
    vec2 uv,
    float density,
    float seed,
    float minLength,
    float maxLength,
    float halfWidth,
    float occupancy,
    float crossChance
  ) {
    vec2 grid = uv * density;
    vec2 baseCell = floor(grid);
    vec2 local = fract(grid);
    float mask = 0.0;

    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 offset = vec2(float(i), float(j));
        vec2 neighbor = baseCell + offset;
        float enabled = step(hash21(neighbor + vec2(seed * 0.37, seed * 0.53)), occupancy);
        vec2 point = hash22(neighbor + seed);
        vec2 center = offset + point;
        float angle = hash21(neighbor + vec2(seed * 1.11, seed * 1.67)) * PI * 2.0;
        float lengthValue = mix(minLength, maxLength, hash21(neighbor + vec2(seed * 2.03, seed * 2.41)));
        vec2 direction = vec2(cos(angle), sin(angle)) * lengthValue * 0.5;
        mask = max(mask, segmentMask(local, center - direction, center + direction, halfWidth) * enabled);

        float hasCross = step(crossChance, hash21(neighbor + vec2(seed * 2.77, seed * 3.19)));
        if (hasCross > 0.0) {
          vec2 crossDir = vec2(-direction.y, direction.x) * mix(0.45, 0.7, hash21(neighbor + vec2(seed * 3.53, seed * 3.97)));
          mask = max(mask, segmentMask(local, center - crossDir, center + crossDir, halfWidth * 0.82) * enabled);
        }
      }
    }

    return mask;
  }

  float woodContourField(vec2 uv) {
    vec2 p = uv * PI;
    vec2 warped = vec2(
      p.x + 0.42 * sin(p.y * 1.08 + 0.34 * cos(p.x * 0.72 + 0.45)),
      p.y + 0.36 * sin(p.x * 0.94 - 0.38 * cos(p.y * 0.78 + 1.1))
    );

    return
      0.62 * sin(warped.x) +
      0.52 * sin(warped.y + 0.24 * cos(warped.x * 0.82)) +
      0.24 * sin(warped.x * 0.74 + warped.y * 0.98 + 0.38);
  }

  float ellipseRingMask(vec2 delta, float radiusX, float radiusY, float ringHalfWidth) {
    float ellipse = length(vec2(delta.x / radiusX, delta.y / radiusY));
    float distanceValue = abs(ellipse - 1.0) * min(radiusX, radiusY);
    return strokeMask(distanceValue, ringHalfWidth);
  }

  float woodGrainKnotMask(vec2 uv, float halfWidth, inout float axisOffset) {
    vec2 grid = uv * vec2(1.55, 0.92);
    vec2 baseCell = floor(grid);
    vec2 local = fract(grid);
    float mask = 0.0;

    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 offset = vec2(float(i), float(j));
        vec2 neighbor = baseCell + offset;
        float enabled = step(0.68, hash21(neighbor + vec2(19.4, 7.1)));
        vec2 point = vec2(
          hash21(neighbor + vec2(3.7, 5.9)),
          hash21(neighbor + vec2(11.2, 13.6))
        );
        vec2 center = offset + vec2(
          mix(0.22, 0.78, point.x),
          mix(0.2, 0.8, point.y)
        );
        vec2 delta = local - center;
        float ellipse = length(vec2(delta.x / 0.085, delta.y / 0.235));
        float influence = exp(-ellipse * ellipse * 1.7) * enabled;
        axisOffset += sign(delta.x) * 0.05 * influence;

        float outerRing = ellipseRingMask(delta, 0.085, 0.235, halfWidth * 0.22);
        float innerRing = ellipseRingMask(delta, 0.05, 0.145, halfWidth * 0.18) * 0.78;
        mask = max(mask, max(outerRing, innerRing) * enabled);
      }
    }

    return mask;
  }

  float woodPattern(vec2 uv, float halfWidth) {
    uv *= vec2(1.28, 0.94);
    float axisOffset = 0.0;
    float knotMask = woodGrainKnotMask(uv, halfWidth, axisOffset);
    float axis = uv.x
      + axisOffset
      + 0.06 * sin(uv.y * PI * 1.12)
      + 0.018 * sin(uv.y * PI * 4.2 + 0.8)
      + 0.01 * sin((uv.y + uv.x * 0.22) * PI * 9.0 + 1.9);
    float primary = lineStripeMask(axis, 0.165, halfWidth * 0.28);
    float secondary = lineStripeMask(
      axis + 0.038 + 0.01 * sin(uv.y * PI * 1.7 + 0.6),
      0.33,
      halfWidth * 0.2
    );
    float tertiary = lineStripeMask(
      axis - 0.026 + 0.007 * sin(uv.y * PI * 2.4 + 2.1),
      0.495,
      halfWidth * 0.15
    );
    return max(max(primary, max(secondary * 0.92, tertiary * 0.72)), knotMask * 0.96);
  }

  float woodContourPattern(vec2 uv, float halfWidth) {
    float field = woodContourField(uv * 1.45);
    float primary = lineStripeMask(field, 0.22, halfWidth * 0.28);
    float secondary = lineStripeMask(
      field + 0.065 * sin(uv.x * PI * 1.3 + uv.y * PI * 0.7),
      0.44,
      halfWidth * 0.2
    );
    return max(primary, secondary * 0.72);
  }

  float grassPattern(vec2 uv, float halfWidth) {
    vec2 gridUv = uv * vec2(6.0, 7.0);
    float row = floor(gridUv.y);
    gridUv.x += 0.5 * mod(row, 2.0);
    vec2 local = fract(gridUv);
    vec2 base = vec2(0.5, 0.58);
    float mask = 0.0;
    mask = max(mask, segmentMask(local, base, vec2(0.34, 0.18), halfWidth));
    mask = max(mask, segmentMask(local, base, vec2(0.5, 0.1), halfWidth));
    mask = max(mask, segmentMask(local, base, vec2(0.66, 0.18), halfWidth));
    return mask;
  }

  float brickPattern(vec2 uv, float halfWidth) {
    vec2 gridScale = vec2(2.75, 8.25);
    vec2 gridUv = uv * gridScale;
    float row = floor(gridUv.y);
    vec2 local = vec2(fract(gridUv.x + 0.5 * mod(row, 2.0)), fract(gridUv.y));
    float mortarHalfWidth = halfWidth * 0.55;
    float joints = min(local.x, 1.0 - local.x) / gridScale.x;
    float beds = min(local.y, 1.0 - local.y) / gridScale.y;
    return max(strokeMask(joints, mortarHalfWidth), strokeMask(beds, mortarHalfWidth));
  }

  float sandPattern(vec2 uv, float halfWidth) {
    float mask = 0.0;
    mask = max(mask, granularDotPattern(uv, 28.0, halfWidth * 1.22, 3.7, 0.985, 0.95) * 1.0);
    mask = max(mask, granularDotPattern(uv * 1.41 + vec2(1.9, 2.7), 22.0, halfWidth * 0.88, 8.4, 0.92, 0.96) * 0.72);
    mask = max(mask, randomDotPattern(uv * 0.83 + vec2(4.2, 1.6), 14.0, halfWidth * 0.75, 12.9) * 0.35);
    return mask;
  }

  float cementPattern(vec2 uv, float halfWidth) {
    float mask = 0.0;
    mask = max(mask, scatteredDashPattern(uv, 13.5, 11.3, 0.11, 0.2, halfWidth * 1.34, 0.94, 0.74));
    mask = max(mask, scatteredDashPattern(uv * 1.29 + vec2(0.37, 1.19), 9.5, 23.1, 0.09, 0.16, halfWidth * 1.12, 0.8, 0.84) * 0.95);
    mask = max(mask, randomDotPattern(uv * 0.92 + vec2(2.7, 4.1), 10.5, halfWidth * 1.15, 31.7) * 0.72);
    return mask;
  }

  float concretePattern(vec2 uv, float halfWidth) {
    vec2 grid = uv * 7.4;
    vec2 cell = floor(grid);
    vec2 local = fract(grid);
    float mask = 0.0;
    mask = max(mask, granularDotPattern(uv, 17.0, halfWidth * 3.4, 17.2, 0.995, 0.42) * 1.0);
    mask = max(mask, granularDotPattern(uv, 24.0, halfWidth * 2.85, 29.7, 0.93, 0.54) * 1.0);
    mask = max(mask, granularDotPattern(uv, 11.5, halfWidth * 3.75, 41.3, 0.82, 0.62) * 0.98);
    mask = max(mask, randomDotPattern(uv, 9.0, halfWidth * 2.7, 53.1) * 0.9);

    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 offset = vec2(float(i), float(j));
        vec2 neighbor = cell + offset;
        float enabled = step(hash21(neighbor + vec2(63.4, 9.1)), 0.76);
        vec2 point = hash22(neighbor + 21.7);
        vec2 center = offset + point;
        float angle = hash22(neighbor + 14.2).x * PI;
        float size = 0.075 + hash22(neighbor + 27.9).x * 0.04;
        mask = max(mask, triangleMask(local, center, size, angle, halfWidth * 0.92) * enabled);
      }
    }

    return mask;
  }

  float stonePattern(vec2 uv, float gridCount, float edgeWidth) {
    vec2 grid = uv * gridCount;
    vec2 cell = floor(grid);
    vec2 local = fract(grid);
    float nearest = 1e9;
    float secondNearest = 1e9;

    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 offset = vec2(float(i), float(j));
        vec2 neighbor = cell + offset;
        vec2 point = hash22(neighbor + 31.4);
        vec2 delta = offset + point - local;
        float distanceValue = dot(delta, delta);

        if (distanceValue < nearest) {
          secondNearest = nearest;
          nearest = distanceValue;
        } else if (distanceValue < secondNearest) {
          secondNearest = distanceValue;
        }
      }
    }

    float edge = sqrt(secondNearest) - sqrt(nearest);
    float aa = max(patternAA() * 1.5, 0.0015);
    return 1.0 - smoothstep(edgeWidth - aa, edgeWidth + aa, edge);
  }

  float legacyEarthPattern(vec2 uv, float halfWidth) {
    vec2 local = fract(uv * 2.2);
    float mask = 0.0;
    mask = max(mask, rectBorderMask(local, vec2(0.0, 0.0), vec2(0.25, 0.5), halfWidth));
    mask = max(mask, rectBorderMask(local, vec2(0.25, 0.0), vec2(0.5, 0.5), halfWidth));
    mask = max(mask, rectBorderMask(local, vec2(0.5, 0.0), vec2(1.0, 0.25), halfWidth));
    mask = max(mask, rectBorderMask(local, vec2(0.5, 0.25), vec2(1.0, 0.5), halfWidth));
    mask = max(mask, rectBorderMask(local, vec2(0.0, 0.5), vec2(0.5, 0.75), halfWidth));
    mask = max(mask, rectBorderMask(local, vec2(0.0, 0.75), vec2(0.5, 1.0), halfWidth));
    mask = max(mask, rectBorderMask(local, vec2(0.5, 0.5), vec2(0.75, 1.0), halfWidth));
    mask = max(mask, rectBorderMask(local, vec2(0.75, 0.5), vec2(1.0, 1.0), halfWidth));
    return mask;
  }

  float earthPattern(vec2 uv, float halfWidth) {
    vec2 gridUv = uv * 4.8;
    vec2 cell = floor(gridUv);
    vec2 local = fract(gridUv);
    float aa = coordAA(gridUv);
    float mask = 0.0;
    float orientation = mod(cell.x + cell.y, 2.0);
    float bundleInset = 0.14;
    float bundleStart = 0.18;
    float bundleEnd = 0.82;
    float bundleHalfWidth = halfWidth * 0.82;

    if (orientation < 1.0) {
      for (int i = 0; i < 4; i++) {
        float x = bundleStart + float(i) * 0.16;
        mask = max(mask, segmentMaskAA(local, vec2(x, bundleInset), vec2(x, 1.0 - bundleInset), bundleHalfWidth, aa));
      }
    } else {
      for (int i = 0; i < 4; i++) {
        float y = bundleStart + float(i) * 0.16;
        mask = max(mask, segmentMaskAA(local, vec2(bundleInset, y), vec2(1.0 - bundleInset, y), bundleHalfWidth, aa));
      }
    }

    return mask;
  }

  float concreteBlockPattern(vec2 uv, float halfWidth) {
    float mask = lineStripeMask(dot(uv, normalize(vec2(1.0, 1.0))), 0.085, halfWidth * 0.48);
    mask = max(mask, lineStripeMask(dot(uv, normalize(vec2(1.0, -1.0))), 0.085, halfWidth * 0.48));
    return mask;
  }

  float steelPattern(vec2 uv, float halfWidth) {
    float axis = dot(uv, normalize(vec2(1.0, 1.0)));
    float mask = lineStripeMask(axis, 0.13, halfWidth * 0.48);
    mask = max(mask, lineStripeMask(axis + 0.042, 0.13, halfWidth * 0.48));
    return mask;
  }

  float glassPattern(vec2 uv, float halfWidth) {
    vec2 local = fract(uv);
    float mask = 0.0;
    mask = max(mask, segmentMask(local, vec2(0.13, 0.28), vec2(0.34, 0.07), halfWidth));
    mask = max(mask, segmentMask(local, vec2(0.19, 0.30), vec2(0.40, 0.09), halfWidth));
    mask = max(mask, segmentMask(local, vec2(0.60, 0.88), vec2(0.81, 0.67), halfWidth));
    mask = max(mask, segmentMask(local, vec2(0.66, 0.90), vec2(0.87, 0.69), halfWidth));
    mask = max(mask, segmentMask(local, vec2(0.38, 0.58), vec2(0.59, 0.37), halfWidth));
    mask = max(mask, segmentMask(local, vec2(0.44, 0.60), vec2(0.65, 0.39), halfWidth));
    return mask;
  }

  float rccPattern(vec2 uv, float halfWidth) {
    return lineStripeMask(dot(uv, normalize(vec2(1.0, 1.0))), 0.085, halfWidth * 0.48);
  }

  float paversOctagonPattern(vec2 uv, float halfWidth) {
    vec2 local = fract(uv * 2.15);
    float mask = 0.0;
    vec2 p0 = vec2(0.22, 0.0);
    vec2 p1 = vec2(0.78, 0.0);
    vec2 p2 = vec2(1.0, 0.22);
    vec2 p3 = vec2(1.0, 0.78);
    vec2 p4 = vec2(0.78, 1.0);
    vec2 p5 = vec2(0.22, 1.0);
    vec2 p6 = vec2(0.0, 0.78);
    vec2 p7 = vec2(0.0, 0.22);
    mask = max(mask, segmentMask(local, p0, p1, halfWidth));
    mask = max(mask, segmentMask(local, p1, p2, halfWidth));
    mask = max(mask, segmentMask(local, p2, p3, halfWidth));
    mask = max(mask, segmentMask(local, p3, p4, halfWidth));
    mask = max(mask, segmentMask(local, p4, p5, halfWidth));
    mask = max(mask, segmentMask(local, p5, p6, halfWidth));
    mask = max(mask, segmentMask(local, p6, p7, halfWidth));
    mask = max(mask, segmentMask(local, p7, p0, halfWidth));
    return mask;
  }

  float pavementGridPattern(vec2 uv, float halfWidth) {
    vec2 gridUv = uv * vec2(3.8, 1.9);
    float column = floor(gridUv.x);
    gridUv.y += 0.5 * mod(column, 2.0);
    vec2 local = fract(gridUv);
    return rectBorderMaskAA(local, vec2(0.0), vec2(1.0), halfWidth, coordAA(gridUv));
  }

  float textureBackedPattern(vec2 uv) {
    if (!usePatternTexture) {
      return 0.0;
    }

    return 1.0 - texture2D(patternTexture, uv).r;
  }

  float basketweavePattern(vec2 uv, float halfWidth) {
    if (usePatternTexture) {
      return textureBackedPattern(uv);
    }

    const float invSqrt2 = 0.70710678;
    const float weaveScale = 2.82842712;
    vec2 repeat = vec2(1.0, 1.0);
    vec2 rotatedUv = vec2(
      dot(uv, vec2(invSqrt2, invSqrt2)),
      dot(uv, vec2(-invSqrt2, invSqrt2))
    );
    vec2 local = fract(rotatedUv * weaveScale);
    float mask = 0.0;

    mask = max(mask, wrappedRectBorderMask(local, vec2(0.5, 0.0), vec2(0.75, 0.75), halfWidth, repeat));
    mask = max(mask, wrappedRectBorderMask(local, vec2(0.25, 0.0), vec2(0.5, 0.25), halfWidth, repeat));
    mask = max(mask, wrappedRectBorderMask(local, vec2(0.75, 0.0), vec2(1.0, 0.25), halfWidth, repeat));
    mask = max(mask, wrappedRectBorderMask(local, vec2(0.75, 0.25), vec2(1.5, 0.5), halfWidth, repeat));
    mask = max(mask, wrappedRectBorderMask(local, vec2(0.0, 0.5), vec2(0.25, 1.25), halfWidth, repeat));
    mask = max(mask, wrappedRectBorderMask(local, vec2(0.25, 0.5), vec2(0.5, 0.75), halfWidth, repeat));
    mask = max(mask, wrappedRectBorderMask(local, vec2(0.75, 0.5), vec2(1.0, 0.75), halfWidth, repeat));
    mask = max(mask, wrappedRectBorderMask(local, vec2(0.25, 0.75), vec2(1.0, 1.0), halfWidth, repeat));

    return mask;
  }

  float runningPaversPattern(vec2 uv, float halfWidth) {
    return usePatternTexture
      ? textureBackedPattern(uv)
      : legacyEarthPattern(uv * 0.9, halfWidth);
  }

  float staggeredPaversPattern(vec2 uv, float halfWidth) {
    vec2 repeat = vec2(5.0, 5.0);
    vec2 paverUv = uv * 7.0;
    vec2 local = mod(paverUv, repeat);
    float aa = coordAA(paverUv);
    float mask = 0.0;

    mask = max(mask, wrappedRectBorderMaskAA(local, vec2(0.0, 0.0), vec2(2.0, 2.0), halfWidth, repeat, aa));
    mask = max(mask, wrappedRectBorderMaskAA(local, vec2(2.0, 1.0), vec2(4.0, 3.0), halfWidth, repeat, aa));
    mask = max(mask, wrappedRectBorderMaskAA(local, vec2(4.0, 2.0), vec2(6.0, 4.0), halfWidth, repeat, aa));
    mask = max(mask, wrappedRectBorderMaskAA(local, vec2(1.0, 3.0), vec2(3.0, 5.0), halfWidth, repeat, aa));
    mask = max(mask, wrappedRectBorderMaskAA(local, vec2(3.0, 4.0), vec2(5.0, 6.0), halfWidth, repeat, aa));

    mask = max(mask, rectBorderMaskAA(local, vec2(2.0, 0.0), vec2(3.0, 1.0), halfWidth, aa));
    mask = max(mask, rectBorderMaskAA(local, vec2(4.0, 1.0), vec2(5.0, 2.0), halfWidth, aa));
    mask = max(mask, rectBorderMaskAA(local, vec2(1.0, 2.0), vec2(2.0, 3.0), halfWidth, aa));
    mask = max(mask, rectBorderMaskAA(local, vec2(3.0, 3.0), vec2(4.0, 4.0), halfWidth, aa));
    mask = max(mask, rectBorderMaskAA(local, vec2(0.0, 4.0), vec2(1.0, 5.0), halfWidth, aa));

    return mask;
  }

  float angledParquetPattern(vec2 uv, float halfWidth, float parquetScale, float plankStep) {
    const float invSqrt2 = 0.70710678;

    float axisA = dot(uv, vec2(invSqrt2, invSqrt2)) * parquetScale;
    float axisB = dot(uv, vec2(-invSqrt2, invSqrt2)) * parquetScale;
    float cellA = floor(axisA);
    float cellB = floor(axisB);
    float localA = fract(axisA);
    float localB = fract(axisB);
    float parity = mod(cellA + cellB, 2.0);
    float mask = 0.0;

    if (parity < 1.0) {
      mask = max(mask, lineStripeMaskAA(localB, plankStep, halfWidth, scalarAA(axisB)));
      mask = max(mask, strokeMaskAA(min(localA, 1.0 - localA), halfWidth, scalarAA(axisA)));
    } else {
      mask = max(mask, lineStripeMaskAA(localA, plankStep, halfWidth, scalarAA(axisA)));
      mask = max(mask, strokeMaskAA(min(localB, 1.0 - localB), halfWidth, scalarAA(axisB)));
    }

    return mask;
  }

  float herringbonePattern(vec2 uv, float halfWidth) {
    vec2 gridUv = uv * vec2(3.8, 1.82);
    const float invSqrt2 = 0.70710678;
    const float columnRise = 0.48;
    const float plankPitch = columnRise * invSqrt2 * 0.8;
    float column = floor(gridUv.x);
    float localX = fract(gridUv.x);
    float direction = mod(column, 2.0) < 1.0 ? 1.0 : -1.0;
    float phase = mod(column, 2.0) * mod(columnRise, plankPitch);
    float diagonalAxis = gridUv.y - direction * columnRise * localX - phase;
    float mask = 0.0;
    float aa = coordAA(gridUv);
    float diagonalAA = scalarAA(diagonalAxis);
    float verticalHalfWidth = halfWidth * 0.95;
    float diagonalHalfWidth = halfWidth * 0.88;

    mask = max(mask, strokeMaskAA(min(localX, 1.0 - localX), verticalHalfWidth, aa));
    mask = max(mask, lineStripeMaskAA(diagonalAxis, plankPitch, diagonalHalfWidth, diagonalAA));

    return mask;
  }

  float herringbone2PlankDistance(vec2 p, vec2 center, float angleSign, vec2 halfSize) {
    const float invSqrt2 = 0.70710678;
    vec2 local = p - center;
    float along = (local.x + angleSign * local.y) * invSqrt2;
    float across = (-angleSign * local.x + local.y) * invSqrt2;
    vec2 delta = abs(vec2(along, across)) - halfSize;
    return max(delta.x, delta.y);
  }

  float herringbone2BoundaryDistance(vec2 uv) {
    const float columnPitch = 0.25;
    const float rowStep = 0.125;
    const float phaseOffset = 0.0625;
    const float startX = 0.125;
    const float startY = 0.0625;
    const vec2 plankHalfSize = vec2(0.15625, 0.03515625);

    float baseColumn = floor((uv.x - startX) / columnPitch);
    float bestDistance = 100000.0;
    float secondBestDistance = 100000.0;

    for (int columnOffset = -2; columnOffset <= 2; columnOffset++) {
      float column = baseColumn + float(columnOffset);
      float positiveSlope = mod(column, 2.0) < 1.0 ? 1.0 : 0.0;
      float angleSign = positiveSlope > 0.5 ? 1.0 : -1.0;
      float phase = positiveSlope > 0.5 ? 0.0 : phaseOffset;
      float centerX = startX + column * columnPitch;
      float baseRow = floor((uv.y - startY - phase) / rowStep);

      for (int rowOffset = -3; rowOffset <= 3; rowOffset++) {
        float row = baseRow + float(rowOffset);
        vec2 center = vec2(centerX, startY + row * rowStep + phase);
        float distanceValue = herringbone2PlankDistance(uv, center, angleSign, plankHalfSize);

        if (distanceValue < bestDistance) {
          secondBestDistance = bestDistance;
          bestDistance = distanceValue;
        } else if (distanceValue < secondBestDistance) {
          secondBestDistance = distanceValue;
        }
      }
    }

    return max(secondBestDistance - bestDistance, 0.0) * 0.5;
  }

  float herringbone2Pattern(vec2 uv, float halfWidth) {
    return strokeMask(herringbone2BoundaryDistance(uv), halfWidth * 0.92);
  }

  float chevronPattern(vec2 uv, float halfWidth) {
    const float chevronScale = 4.4;
    const float plankStep = 0.25;
    return angledParquetPattern(uv, halfWidth, chevronScale, plankStep);
  }

  float chevronLinesPattern(vec2 uv, float halfWidth) {
    uv *= 1.9;
    vec2 cell = floor(uv);
    float mask = 0.0;

    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 base = cell + vec2(float(i), float(j));
        mask = max(mask, segmentMask(uv, base + vec2(0.5, 0.0), base + vec2(1.0, 0.5), halfWidth));
        mask = max(mask, segmentMask(uv, base + vec2(0.5, 0.0), base + vec2(0.0, 0.5), halfWidth));
      }
    }

    return mask;
  }

  float getPatternMask(int selectedPattern, vec2 uv, float halfWidth) {
    if (selectedPattern == 1) return woodPattern(uv, halfWidth);
    if (selectedPattern == 2) return woodContourPattern(uv, halfWidth);
    if (selectedPattern == 3) return grassPattern(uv, halfWidth);
    if (selectedPattern == 4) return brickPattern(uv, halfWidth);
    if (selectedPattern == 5) return sandPattern(uv, halfWidth);
    if (selectedPattern == 6) return cementPattern(uv, halfWidth);
    if (selectedPattern == 7) return concretePattern(uv, halfWidth);
    if (selectedPattern == 8) return stonePattern(uv, 6.2, 0.009);
    if (selectedPattern == 9) return stonePattern(uv, 10.5, 0.014);
    if (selectedPattern == 10) return earthPattern(uv, halfWidth);
    if (selectedPattern == 11) return concreteBlockPattern(uv, halfWidth);
    if (selectedPattern == 12) return steelPattern(uv, halfWidth);
    if (selectedPattern == 13) return glassPattern(uv, halfWidth);
    if (selectedPattern == 14) return rccPattern(uv, halfWidth);
    if (selectedPattern == 15) return paversOctagonPattern(uv, halfWidth);
    if (selectedPattern == 16) return pavementGridPattern(uv, halfWidth);
    if (selectedPattern == 17) return basketweavePattern(uv, halfWidth);
    if (selectedPattern == 18) return runningPaversPattern(uv, halfWidth);
    if (selectedPattern == 19) return staggeredPaversPattern(uv, halfWidth);
    if (selectedPattern == 20) return herringbonePattern(uv, halfWidth);
    if (selectedPattern == 21) return chevronPattern(uv, halfWidth);
    if (selectedPattern == 22) return chevronLinesPattern(uv, halfWidth);
    if (selectedPattern == 23) return herringbone2Pattern(uv, halfWidth);
    return 0.0;
  }

  void main() {
    vec2 uv = vPatternCoord / patternScale;
    float halfWidth = strokeWidth / patternScale;
    float mask = getPatternMask(patternType, uv, halfWidth);
    vec3 lineColor = fillColor * (1.0 - lineDarkness);
    vec3 color = mix(fillColor, lineColor, clamp(mask, 0.0, 1.0));

    gl_FragColor = vec4(color, opacity);

    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

const patternFunctionsStart = patternFillFragmentShader.indexOf(
  '  float patternAA()'
);
const patternFunctionsEnd = patternFillFragmentShader.lastIndexOf(
  '  void main() {'
);

/**
 * Shared procedural pattern functions for lit 3D materials.
 *
 * The opening PI constant is intentionally excluded because Three.js' standard
 * shader already defines PI in its common shader chunk.
 */
export const patternFillProceduralShader = patternFillFragmentShader.slice(
  patternFunctionsStart,
  patternFunctionsEnd
);

// Cache for shader materials to avoid recreating them
const shaderMaterialCache = new Map<string, THREE.ShaderMaterial>();

/**
 * Create a cache key for shader materials.
 */
function getCacheKey(
  fillColor: number,
  fillPattern: FillPattern,
  useWorldSpace: boolean,
  useLocalPositionCoords: boolean,
  side: THREE.Side,
  opacity: number,
  transparent: boolean,
  depthWrite: boolean,
  depthTest: boolean
): string {
  return `${fillColor}-${fillPattern}-${useWorldSpace}-${useLocalPositionCoords}-${side}-${opacity}-${transparent}-${depthWrite}-${depthTest}`;
}

/**
 * Options for creating fill materials.
 */
export interface FillMaterialOptions {
  side?: THREE.Side;
  opacity?: number;
  transparent?: boolean;
  depthWrite?: boolean;
  depthTest?: boolean;
  useWorldSpace?: boolean;
  useLocalPositionCoords?: boolean;
}

/**
 * Factory function to create fill materials.
 * Returns MeshBasicMaterial when no pattern is set (zero overhead for solid fills).
 * Returns PatternFillMaterial (ShaderMaterial) when pattern is set.
 */
export function createFillMaterial(
  fillColor: number | undefined,
  fillPattern: FillPattern | undefined,
  options: FillMaterialOptions = {}
): THREE.Material {
  const patternDefinition = getFillPatternDefinition(fillPattern);
  const {
    side = THREE.DoubleSide,
    opacity = 1,
    transparent = false,
    depthWrite = true,
    depthTest = true,
    useWorldSpace = true,
    useLocalPositionCoords = false,
  } = options;

  // If no pattern, return simple MeshBasicMaterial
  if (!patternDefinition) {
    return new THREE.MeshBasicMaterial({
      color: fillColor,
      side,
      opacity,
      transparent,
      depthWrite,
      depthTest,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
  }

  // Check cache for existing material
  const cacheKey = getCacheKey(
    fillColor ?? 0xffffff,
    patternDefinition.id,
    useWorldSpace,
    useLocalPositionCoords,
    side,
    opacity,
    transparent,
    depthWrite,
    depthTest
  );

  if (shaderMaterialCache.has(cacheKey)) {
    return shaderMaterialCache.get(cacheKey)!;
  }

  const patternTexture = patternDefinition.id === 'basketweave' || patternDefinition.id === 'runningPavers'
    ? getPatternTexture(patternDefinition.id)
    : null;

  // Create new shader material
  const material = new THREE.ShaderMaterial({
    uniforms: {
      patternScale: { value: patternDefinition.worldScale },
      strokeWidth: { value: patternDefinition.strokeWidth },
      patternType: { value: patternDefinition.shaderType },
      patternTexture: { value: patternTexture },
      usePatternTexture: { value: patternTexture !== null },
      fillColor: { value: new THREE.Color(fillColor ?? 0xffffff) },
      lineDarkness: { value: 0.62 },
      opacity: { value: opacity },
      useWorldSpace: { value: useWorldSpace },
      useLocalPositionCoords: { value: useLocalPositionCoords },
    },
    vertexShader: patternFillVertexShader,
    fragmentShader: patternFillFragmentShader,
    side,
    transparent,
    depthWrite,
    depthTest,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  (material as THREE.ShaderMaterial & { extensions: { derivatives?: boolean } }).extensions.derivatives = true;

  // Cache the material
  shaderMaterialCache.set(cacheKey, material);

  return material;
}

/**
 * Dispose all cached shader materials.
 * Call this during app cleanup or HMR.
 */
export function disposeAllPatternFillMaterials(): void {
  shaderMaterialCache.forEach((material) => {
    material.dispose();
  });
  shaderMaterialCache.clear();
}
